/**
 * Field metadata for the LBA form — which catalogue key labels a field id, and
 * what kind of value it holds. No prose lives here: `label(id, t)` composes the
 * text from the i18n catalogue, so the same id reads German or English.
 */

import type { T } from './i18n';

export const LEG_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type LegNumber = (typeof LEG_NUMBERS)[number];
/** Route rows in the order the form (and the VAR carry-down) reads them. */
export const ROUTE_ROWS: (LegNumber | 'alt')[] = [...LEG_NUMBERS, 'alt'];

export const legField = (j: LegNumber | 'alt', k: string): string => (j === 'alt' ? 'alt_' + k : 'leg' + j + '_' + k);

/** Field ids whose label is a single catalogue entry, `lbl.<id>`. */
const DIRECT = new Set([
  'datum', 'von', 'nach', 'lfz_muster', 'lfz_kennz', 'etd', 'atd', 'eta', 'ata', 'ss', 'safe_time',
  'total_dist', 'total_time', 'gafor', 'gafor_check', 'gafor_bis', 'fuel_vorrat_zeit', 'fuel_safe_time',
  'alt_name',
]);

/** Route-row keys (`leg.<key>`), shared by leg1…leg12 and the alternate row. */
const LEG_KEYS = new Set([
  'freq', 'safealt', 'alt', 'tas', 'wind', 'tc', 'wca', 'var', 'mh', 'mc',
  'dist', 'rest_dist', 'gs', 'time', 'rest_time', 'remark',
]);
const FQ_KEYS = new Set(['ground', 'twr', 'info']);
const ATIS_KEYS = new Set([
  'station', 'code', 'rwy', 'tl', 'zeit', 'wind', 'sicht', 'wolken', 'temp', 'qnh', 'tendenz',
]);
const AIP_KEYS = new Set([
  'rwy', 'len', 'dist15', 'elev', 'temp', 'wind', 'slope', 'belag', 'gras', 'surface', 'restr',
]);
const FUEL_KEYS = new Set([
  'reise', 'anlassen', 'steig', 'anab', 'altn', 'reserve', 'mindest', 'extra', 'vorrat',
]);
const MB_KEYS = new Set([
  'leer', 'kna', 'schmier', 'grund', 'sitz1', 'sitz2', 'sitz3',
  'gep_vorn', 'gep_mitte', 'gep_hinten', 'kausf', 'start',
]);
const TL_KEYS = new Set([
  'bahn', 'verf', 'grund', 'hoehe', 'temp', 'wind', 'neig', 'gras', 'oberfl', 'benoetigt',
]);

/** Capture group as a string — the match arrays are all `string | undefined` here. */
const g = (m: RegExpMatchArray, i: number): string => m[i] ?? '';

/** Translate `<prefix>.<key>`, or fall back to the raw key for an id we do not know. */
const part = (t: T, prefix: string, key: string, known: Set<string>): string =>
  known.has(key) ? t(prefix + '.' + key) : key;

/** Human label for a field id — used for aria-label and the tooltip. */
export function label(id: string, t: T): string {
  if (DIRECT.has(id)) return t('lbl.' + id);
  let m: RegExpMatchArray | null;
  if ((m = id.match(/^freigaben_(\d)/))) return t('lbl.freigabenRow', { n: g(m, 1) });
  if ((m = id.match(/^fq_(dep|dest)_(\w+)/))) {
    return t('lbl.fqRow', { where: t('fq.' + g(m, 1)), what: part(t, 'fq', g(m, 2), FQ_KEYS) });
  }
  if ((m = id.match(/^atis(\d)_(\w+)/))) {
    return t('lbl.atisRow', { n: g(m, 1), what: part(t, 'atis', g(m, 2), ATIS_KEYS) });
  }
  if ((m = id.match(/^leg(\d+)_(\w+)/))) {
    return t('lbl.legRow', { n: g(m, 1), what: part(t, 'leg', g(m, 2), LEG_KEYS) });
  }
  if ((m = id.match(/^alt_(\w+)/)) && LEG_KEYS.has(g(m, 1))) {
    return t('lbl.altRow', { what: t('leg.' + g(m, 1)) });
  }
  if ((m = id.match(/^cp(\d+)_(name|freq|eto|ato)/))) {
    return t('lbl.cpRow', { what: t('cp.' + g(m, 2)), n: g(m, 1) });
  }
  if ((m = id.match(/^fs_beratung_(\d)/))) return t('lbl.fsRow', { n: g(m, 1) });
  if ((m = id.match(/^wetter_(\d)/))) return t('lbl.wxRow', { n: g(m, 1) });
  if ((m = id.match(/^aip_(p1|p2f|p20)_(\w+)/))) {
    return t('lbl.aipRow', { where: t('aip.' + g(m, 1)), what: part(t, 'aip', g(m, 2), AIP_KEYS) });
  }
  if ((m = id.match(/^fuel_(\w+?)_(zeit|l)$/))) {
    return t('lbl.fuelRow', {
      row: part(t, 'fuel', g(m, 1), FUEL_KEYS),
      what: g(m, 2) === 'zeit' ? t('fuel.zeit') : t('fuel.liter'),
    });
  }
  if ((m = id.match(/^fuel_verbrauch_(\d)/))) return t('lbl.verbrauchRow', { n: g(m, 1) });
  if ((m = id.match(/^mb_unit_(\w+)/))) {
    return t('lbl.mbUnit', { what: g(m, 1) === 'masse' ? t('mb.masse') : t('mb.moment') });
  }
  if ((m = id.match(/^mb_(\w+?)_(m|mom)$/))) {
    return t('lbl.mbRow', {
      row: part(t, 'mb', g(m, 1), MB_KEYS),
      what: g(m, 2) === 'm' ? t('mb.masse') : t('mb.moment'),
    });
  }
  if ((m = id.match(/^tl_(\w+?)_(sr|ss|ll|lr)$/))) {
    return t('lbl.tlRow', { row: part(t, 'tl', g(m, 1), TL_KEYS), col: t('tl.' + g(m, 2)) });
  }
  return id;
}

/* ------------------------------------------------------------------ *
 * Field kinds: what a field may contain, how it is tidied up on blur
 * and what counts as malformed. The STORED string format is the paper
 * form's — the kind never invents a format the PDF cannot stamp.
 * ------------------------------------------------------------------ */

export type FieldKind =
  | 'time' | 'hhmm' | 'date' | 'int' | 'decimal' | 'heading' | 'wind'
  | 'wca' | 'icao' | 'code' | 'freq' | 'signed' | 'text';

/** Route rows (leg1…leg12 and the alternate). `alt` (Höhe/FL) stays text: "65", "FL 65", "3.0". */
const ROUTE_KINDS: Record<string, FieldKind> = {
  tas: 'int', gs: 'int', time: 'int', rest_time: 'int',
  dist: 'decimal', rest_dist: 'decimal',
  // SafeAlt is written in thousands of feet on this form ("2.5") — decimal, not int.
  safealt: 'decimal',
  tc: 'heading', mh: 'heading', mc: 'heading',
  wind: 'wind', wca: 'wca', var: 'signed', freq: 'freq',
  alt: 'text', remark: 'text', name: 'text',
};

const ATIS_KINDS: Record<string, FieldKind> = {
  zeit: 'hhmm', qnh: 'int', wind: 'wind', code: 'code',
  // "32R" is a legal runway designator, "3 sc 5.5" a legal cloud group: free text.
  rwy: 'text', tl: 'text', station: 'text', sicht: 'text', wolken: 'text',
  temp: 'text', tendenz: 'text',
};

const AIP_KINDS: Record<string, FieldKind> = {
  // `elev` is SIGNED, not `int`: EHAM (-10 ft), EHRD (-13 ft) and every other
  // below-MSL aerodrome publish a negative elevation, and `geo.ts`/`terrain.ts`
  // write it verbatim. Typing it `int` made the app flag a figure it had just
  // derived from the AIP as "keine gültige Zahl".
  len: 'int', dist15: 'int', elev: 'signed',
  temp: 'signed', wind: 'signed', slope: 'signed',
  rwy: 'text', belag: 'text', gras: 'text', surface: 'text', restr: 'text',
};

/** Start-/Landestrecken rows: the four length rows are metres, the corrections may be signed or "enth.". */
const TL_INT_ROWS = /^(bahn|verf|grund|benoetigt)$/;

/** What kind of value a field id holds. Everything not recognised is free text. */
export function kindFor(id: string): FieldKind {
  switch (id) {
    case 'datum': return 'date';
    case 'von': case 'nach': case 'alt_name': return 'icao';
    case 'etd': case 'atd': case 'eta': case 'ata': case 'ss':
    case 'safe_time': case 'fuel_safe_time': case 'gafor_bis': return 'time';
    case 'total_time': return 'int';
    case 'total_dist': return 'decimal';
    default: break;
  }
  let m: RegExpMatchArray | null;
  if ((m = id.match(/^(?:leg\d+|alt)_(\w+)$/))) return ROUTE_KINDS[m[1] ?? ''] ?? 'text';
  if ((m = id.match(/^cp\d+_(name|freq|eto|ato)$/))) {
    const k = m[1];
    return k === 'freq' ? 'freq' : k === 'name' ? 'text' : 'time';
  }
  if ((m = id.match(/^atis\d_(\w+)$/))) return ATIS_KINDS[m[1] ?? ''] ?? 'text';
  if (/^fq_(dep|dest)_(ground|twr|info)$/.test(id)) return 'freq';
  if ((m = id.match(/^aip_(?:p1|p2f|p20)_(\w+)$/))) return AIP_KINDS[m[1] ?? ''] ?? 'text';
  if ((m = id.match(/^fuel_\w+_(zeit|l)$/))) return m[1] === 'zeit' ? 'time' : 'decimal';
  // masses are positive; a moment about the datum may be negative ("-1.5")
  if (/^mb_\w+_m$/.test(id)) return 'decimal';
  if (/^mb_\w+_mom$/.test(id)) return 'signed';
  if ((m = id.match(/^tl_(\w+?)_(sr|ss|ll|lr)$/))) return TL_INT_ROWS.test(m[1] ?? '') ? 'int' : 'signed';
  return 'text';
}

/**
 * Mobile keyboard hints — only for fields that are ALWAYS pure positive numbers
 * or times written as digits. Anything that may carry signs (VAR, wind/slope/
 * temperature corrections, W&B moments), letters (Höhe/FL, idents, ATIS code,
 * CAVOK), slashes or brackets (frequencies: "118.2/122.5", "(489)") keeps the
 * full keyboard.
 */
export function inputModeFor(id: string): 'numeric' | 'decimal' | undefined {
  // The fuel block's duration column is written ":MM" — a colon no keypad has.
  if (/^fuel_\w+_zeit$/.test(id)) return undefined;
  switch (kindFor(id)) {
    case 'time': case 'hhmm': case 'int': case 'heading': return 'numeric';
    case 'decimal': return 'decimal';
    default: return undefined;
  }
}

/* ---------------- normalisation (blur only, never per keystroke) ---------------- */

const pad2 = (s: string): string => (s.length === 1 ? '0' + s : s);
const digits = (s: string): string => String(Number(s));

/** "1130"→"11:30", "930"→"09:30", "9:5"→"09:05", "11.30"→"11:30"; ":07", "50" and junk are left alone. */
function normTime(raw: string): string {
  const v = raw.replace(/\s+/g, '');
  let m: RegExpMatchArray | null;
  if ((m = v.match(/^(\d{1,2})[:.hH](\d{1,2})$/))) return pad2(m[1] ?? '') + ':' + pad2(m[2] ?? '');
  if ((m = v.match(/^:(\d{1,2})$/))) return ':' + pad2(m[1] ?? '');
  if (/^\d{4}$/.test(v)) return v.slice(0, 2) + ':' + v.slice(2);
  if (/^\d{3}$/.test(v)) return '0' + v.slice(0, 1) + ':' + v.slice(1);
  return raw;
}

/** ATIS observation time: four digits. "930"→"0930", "11:20"→"1120". */
function normHhmm(raw: string): string {
  const v = raw.replace(/\s+/g, '');
  const m = v.match(/^(\d{1,2})[:.](\d{2})$/);
  if (m) return pad2(m[1] ?? '') + (m[2] ?? '');
  if (/^\d{3,4}$/.test(v)) return v.padStart(4, '0');
  return raw;
}

/** Comma → dot, no spaces, no dangling decimal point. Never rounds, never wraps. */
function normNumber(raw: string): string {
  let v = raw.replace(/\s+/g, '').replace(/,/g, '.');
  if (v.endsWith('.')) v = v.slice(0, -1);
  return v;
}

/** "27010"→"270/10", "27010G20"→"270/10G20", "VRB03"→"VRB/3", "270 / 10"→"270/10". */
function normWind(raw: string): string {
  const v = raw.replace(/\s+/g, '').toUpperCase();
  let m: RegExpMatchArray | null;
  if ((m = v.match(/^(\d{1,3}|VRB)\/(\d{1,3})(?:G(\d{1,3}))?$/))) {
    return (m[1] ?? '') + '/' + digits(m[2] ?? '') + (m[3] ? 'G' + digits(m[3]) : '');
  }
  if ((m = v.match(/^(\d{3})(\d{2,3})(?:G(\d{2,3}))?$/))) {
    return (m[1] ?? '') + '/' + digits(m[2] ?? '') + (m[3] ? 'G' + digits(m[3]) : '');
  }
  if ((m = v.match(/^VRB(\d{1,3})(?:G(\d{2,3}))?$/))) {
    return 'VRB/' + digits(m[1] ?? '') + (m[2] ? 'G' + digits(m[2]) : '');
  }
  return raw.trim();
}

/** "+5"→"5+", "-3"→"3-". A bare number is ambiguous (left/right unknown) and stays as typed. */
function normWca(raw: string): string {
  const v = raw.replace(/\s+/g, '');
  const m = v.match(/^([+-])(\d+(?:\.\d+)?)$/);
  return m ? (m[2] ?? '') + (m[1] ?? '') : v;
}

/** Two-digit years the way the form's own example does it: 87 → 1987, 26 → 2026. */
const fullYear = (y: number): number => (y >= 100 ? y : y < 70 ? 2000 + y : 1900 + y);

interface Ymd { y: number; mo: number; d: number }

/** "17.8.2026", "17.08.26", "2026-08-17" → {y,mo,d}; null if it is not a date. */
export function parseDateParts(raw: string): Ymd | null {
  const v = raw.trim();
  let m: RegExpMatchArray | null;
  if ((m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    return { y: +(m[1] ?? 0), mo: +(m[2] ?? 0), d: +(m[3] ?? 0) };
  }
  if ((m = v.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{2,4})$/))) {
    return { y: fullYear(+(m[3] ?? 0)), mo: +(m[2] ?? 0), d: +(m[1] ?? 0) };
  }
  return null;
}

const validYmd = (p: Ymd): boolean => {
  if (p.mo < 1 || p.mo > 12 || p.d < 1 || p.y < 1) return false;
  const dt = new Date(Date.UTC(p.y, p.mo - 1, p.d));
  return dt.getUTCFullYear() === p.y && dt.getUTCMonth() === p.mo - 1 && dt.getUTCDate() === p.d;
};

/** The stored date format: "d.m.yyyy", no leading zeros — as the LBA example writes it. */
export function formatDate(p: Ymd): string {
  return p.d + '.' + p.mo + '.' + p.y;
}

/** How far ahead a new flight is put, and to what granularity the time is rounded. */
const NEW_FLIGHT_LEAD_MIN = 10;
const NEW_FLIGHT_STEP_MIN = 5;

/**
 * Datum and ETD for a flight that has just been created: today, ten minutes from
 * now, rounded up to the next five.
 *
 * A blank date is not neutral — it is the one value on the form that half the
 * plan hangs off. With it empty the winds are fetched for "now" and say so, the
 * sunset cannot be computed, and the variation is taken from the current epoch
 * rather than the flight's. A pilot planning next Saturday changes two fields;
 * a pilot planning the next hour changes none.
 *
 * UTC throughout, like every other time on this form.
 */
export function newFlightStart(now: Date): { datum: string; etd: string } {
  const at = new Date(now.getTime() + NEW_FLIGHT_LEAD_MIN * 60_000);
  at.setUTCSeconds(0, 0);
  // rounding up may roll the hour, and the day with it — hence one Date, read after
  const over = at.getUTCMinutes() % NEW_FLIGHT_STEP_MIN;
  if (over) at.setUTCMinutes(at.getUTCMinutes() + (NEW_FLIGHT_STEP_MIN - over));
  const hm =
    String(at.getUTCHours()).padStart(2, '0') + ':' + String(at.getUTCMinutes()).padStart(2, '0');
  return {
    datum: formatDate({ y: at.getUTCFullYear(), mo: at.getUTCMonth() + 1, d: at.getUTCDate() }),
    etd: hm,
  };
}

/** A year typed with two digits stays two digits — the form's own example writes "9.5.87". */
const TWO_DIGIT_YEAR = /^\d{1,2}\.\s*\d{1,2}\.\s*(\d{2})$/;

/** Stored "9.5.87" → "1987-05-09" for <input type="date">; '' when unparseable. */
export function dateToIso(stored: string): string {
  const p = parseDateParts(stored);
  if (!p || !validYmd(p)) return '';
  return String(p.y).padStart(4, '0') + '-' + String(p.mo).padStart(2, '0') + '-' + String(p.d).padStart(2, '0');
}

/** "2026-08-17" from <input type="date"> → the stored "17.8.2026". */
export function isoToDate(iso: string): string {
  const p = parseDateParts(iso);
  return p ? formatDate(p) : '';
}

/**
 * Tidy a value the moment the pilot leaves the field — never while typing.
 * Anything that cannot be understood is returned untouched: a stored value is
 * only ever rewritten into a form the PDF and the calculator both accept.
 */
export function normalize(kind: FieldKind, raw: string): string {
  const v = raw.trim();
  if (v === '') return '';
  switch (kind) {
    case 'time': return normTime(v);
    case 'hhmm': return normHhmm(v);
    case 'date': {
      const p = parseDateParts(v);
      if (!p || !validYmd(p)) return v;
      // only tidy separators/leading zeros — never widen "9.5.87" into "9.5.1987"
      const short = v.match(TWO_DIGIT_YEAR);
      return short ? p.d + '.' + p.mo + '.' + (short[1] ?? '') : formatDate(p);
    }
    case 'int': case 'decimal': case 'heading': return normNumber(v);
    case 'wind': return normWind(v);
    case 'wca': return normWca(v);
    case 'icao': case 'code': return v.toUpperCase();
    case 'freq': return v.replace(/,/g, '.');
    default: return v;
  }
}

/* ---------------- validation (stable keys, DE messages) ---------------- */

const okTime = (v: string): boolean => {
  let m: RegExpMatchArray | null;
  if ((m = v.match(/^(\d{1,2}):(\d{2})$/))) return +(m[1] ?? 0) <= 23 && +(m[2] ?? 0) <= 59;
  // ":30" (a duration on the fuel block) and "50" (minutes past the hour in the ETO column)
  if ((m = v.match(/^:(\d{1,2})$/))) return +(m[1] ?? 0) <= 59;
  if (/^\d{1,2}$/.test(v)) return +v <= 59;
  return false;
};

const okWind = (v: string): boolean => {
  const m = v.match(/^(\d{1,3}|VRB)\/(\d{1,3})(?:G(\d{1,3}))?$/);
  if (!m) return false;
  const dir = m[1] ?? '';
  if (dir !== 'VRB' && +dir > 360) return false;
  if (+(m[2] ?? 0) > 199) return false;
  return !m[3] || +m[3] <= 199;
};

/**
 * `null` when the value is acceptable, otherwise a stable message key.
 * Empty is never invalid — a half-filled form is the normal state in flight.
 */
export function validate(kind: FieldKind, value: string): string | null {
  const v = value.trim();
  if (v === '') return null;
  switch (kind) {
    case 'time': return okTime(v) ? null : 'invalid.time';
    case 'hhmm': return /^\d{4}$/.test(v) && +v.slice(2) <= 59 ? null : 'invalid.hhmm';
    case 'date': {
      const p = parseDateParts(v);
      return p && validYmd(p) ? null : 'invalid.date';
    }
    case 'int': return /^\d+$/.test(v) ? null : 'invalid.number';
    case 'decimal': return /^\d+(\.\d+)?$/.test(v) ? null : 'invalid.number';
    case 'heading': return /^\d{1,3}$/.test(v) && +v <= 360 ? null : 'invalid.heading';
    case 'wind': return okWind(v) ? null : 'invalid.wind';
    case 'wca': return /^\d+(\.\d+)?[+-]?$/.test(v) ? null : 'invalid.wca';
    default: return null;
  }
}

/**
 * Section chips, in the order the predecessor showed them. `code` is the field
 * code printed on the paper form and is language-independent; the title and the
 * nav chip come from the catalogue (`sec.<key>.title` / `sec.<key>.nav`).
 */
export const SECTIONS: { key: string; code: string }[] = [
  { key: 'flug', code: 'V1' },
  { key: 'route', code: 'P1–P21 · B3/B4' },
  { key: 'zeit', code: 'V3/V4 · P19 · B2 · P27' },
  { key: 'wx', code: 'V6' },
  { key: 'atis', code: 'B6' },
  { key: 'b1', code: 'B1' },
  { key: 'v5', code: 'V5' },
  { key: 'fq', code: 'V2' },
  { key: 'aip', code: 'V7' },
  { key: 'fuel', code: 'P22–P27' },
  { key: 'mb', code: 'P28–P32' },
  { key: 'tl', code: 'P33–P35' },
];

/** Sections collapsed by default on narrow screens. */
export const SECONDARY_SECTIONS = ['atis', 'b1', 'v5', 'fq', 'aip', 'fuel', 'mb', 'tl'];
